import { apiCall } from 'app/shared/lib/essette-api';
import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { deepClone } from 'app/shared/lib/utilities';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IPopup } from 'app/shared/elements/popup-base';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { SaveAssociatedProvider } from 'app/core/components/users/save-associated-provider';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { UserService } from 'app/core/services/user-service';
import { validate } from 'app/shared/elements/ess-rule';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { I18N } from 'aurelia-i18n';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

interface CustomProvider extends CD.ProviderUserContext {
    roleCount?: number | null;
    roleToolTipDesc?: string | null;
}
@autoinject()
export class UserAssociatedProviders extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.object()
    public user: Core.User | null = null;

    protected includeInactive = false;
    protected pageSize = 10;
    protected resultCount = 0;
    protected sorting = new Sorting();

    protected associatedProviders: CD.ProviderUserContext[] = [];
    protected locations: CD.ProviderLocationUserDetailModel[] = [];
    protected openProviderSummary = openProviderSummary;
    protected providers: ReadonlyArray<CustomProvider> | null = null;

    protected setRoles = (providers: CustomProvider[]): CustomProvider[] => {
        providers.forEach(provider => {
            provider.roleCount = 0;
            if(provider.roles?.length === 1) { provider.roleCount++; }
            if(Array.isArray(provider.roles) && provider.roles?.length > 1) {
                let listDisplay = '<ul>';
                for(const [role, value] of Object.entries({ ...provider.roles })) {
                    if(value) {
                        provider.roleCount++;
                        listDisplay += '<li>' + (value as Core.UserRole).$display + '</li>';
                    }
                }
                listDisplay += '</ul>';
                provider.roleToolTipDesc = listDisplay;
            }
        });
        return providers;
    }

    protected validate = validate;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $saveAssociatedProvider!: SaveAssociatedProvider;
    protected $saveAssociatedProviderModal!: IPopup;
    protected $form!: IForm;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly userService: UserService,
        private readonly toaster: Toaster,
        protected readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
        this.applyDefaultSort();
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadAssociatedProviders();
    }

    protected applyDefaultSort(): void {
        this.sorting.entries = [
            {
                reference: 'provider.name.last provider.name.first',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected applySort(event: SortChangeEvent): void {
        if(this.resultCount > 1) {
            const { property, direction } = event.detail;
            const { sorting } = this;
            if(direction === null) {
                sorting.remove(property);
                if(this.sorting.entries.length == 0) {
                    this.applyDefaultSort();
                }
            } else {
                sorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            this.reload();
        }
    }

    protected saveAssociatedProvider(associatedProviderContext: CD.ProviderUserContext): void {
        //edit mode
        if(associatedProviderContext) {
            this.$saveAssociatedProvider.editContext = deepClone(associatedProviderContext);
            this.$saveAssociatedProvider.loadAssociatedProviderDataByEditContext();
        }
        this.$saveAssociatedProviderModal.open();
    }

    protected saveAssociatedProviderClose(): void {
        if(!this.$saveAssociatedProvider.associatedProviderLocation) {
            //NOTE: in add mode, if a provider is selected and we hit cancel, reset will happen in the Changed event handler of the associatedProviderLocation
            this.$saveAssociatedProvider.reset();
        }
        this.$saveAssociatedProvider.resetProvider();
        this.$saveAssociatedProviderModal.close();
    }

    protected formSuccess(): void {
        this.$saveAssociatedProviderModal.close();
        this.reload();
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadAssociatedProviders(): Promise<void> {
        if(!this.user?.id) return;
        const fields: string[] = [
            'provider.identifier.primary',
            'provider.name.last',
            'provider.name.first',
            'provider.name.full',
            'provider.primaryUser.id',
            'provider.primaryUser.$display',
            'provider.primaryUser.code',
            'provider.$display',
            'provider.detail.isPrimary',
            'roles.$display',
            'homePage.$display',
            'providerLocations.facility.$display',
            'providerLocations.detail.associatedContact.$display'

        ];
        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        const includes: string[] = [
            'providerLocations.facility',
            'providerLocations.detail.associatedContact',
            'provider',
            'provider.primaryUser',
            'homepage',
            'roles',
        ];

        try {
            this.state.beginLoad();
            const response = await this.userService.loadAssociatedProviders(this.user?.id, options, includes, fields);
            this.associatedProviders = this.setRoles(response.results);
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateAssociatedProvider(associatedProvider: CD.ProviderUserContext, isActive: boolean): Promise<boolean> {
        if(!isActive) {
            const message = this.i18n.tr('ui:messages.actions.deactivate', { what: '$t(cd:provider.associated.$)' })
            const subMessage = this.i18n.tr('ui:messages.warnings.associatedProvider', null);
            const result = await confirmation.showCustom(message, subMessage, confirmation.options.StandardConfirm, false);
            if(!(result.dialogResponse === confirmation.options.OK)) return true;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.userService.activateAssociatedProvider(associatedProvider.id!, isActive);
            isSuccessful = response.isSuccessful;
            if(response.isSuccessful) {
                this.loadAssociatedProviders();
            }
        }
        catch(error) {
            this.toaster.standard.activate('cd:provider.associated.$', isActive, isSuccessful);
            throw error;
        } finally {
            this.toaster.standard.activate('cd:provider.associated.$', isActive, isSuccessful);
            this.state.endSave();
            return isSuccessful;
        }
    }
}
